import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { useRunAdminCommand } from "./useRunAdminCommand";
import { RunAdminCommandResultPopup } from "./RunAdminCommandResultPopup";

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
}

export function FileUploadModal({ open, onClose }: FileUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { executeCommandWithFile, isExecuting, result, clearResult } =
    useRunAdminCommand();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showResultPopup, setShowResultPopup] = useState(false);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);

    // Automatically upload
    const response = await executeCommandWithFile(
      "upload-file-to-s3",
      { folder: "admin-uploads" },
      file,
      "file"
    );

    if (response) {
      setShowResultPopup(true);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    setSelectedFile(file);

    // Automatically upload
    const response = await executeCommandWithFile(
      "upload-file-to-s3",
      { folder: "admin-uploads" },
      file,
      "file"
    );

    if (response) {
      setShowResultPopup(true);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    clearResult();
    onClose();
  };

  const handleResultPopupClose = () => {
    setShowResultPopup(false);
    // Keep the modal open so user can upload another file
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload File to S3</DialogTitle>
        <DialogContent>
          <Box
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              border: "2px dashed",
              borderColor: isExecuting ? "primary.main" : "grey.400",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: isExecuting ? "default" : "pointer",
              backgroundColor: isExecuting
                ? "rgba(25, 118, 210, 0.04)"
                : "transparent",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: isExecuting ? "primary.main" : "primary.light",
                backgroundColor: isExecuting
                  ? "rgba(25, 118, 210, 0.04)"
                  : "rgba(25, 118, 210, 0.04)",
              },
            }}
            onClick={() => !isExecuting && fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
              disabled={isExecuting}
            />

            {isExecuting ? (
              <Box>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1" color="primary">
                  Uploading {selectedFile?.name}...
                </Typography>
                <LinearProgress sx={{ mt: 2, mx: 4 }} />
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon
                  sx={{ fontSize: 48, color: "grey.500", mb: 1 }}
                />
                <Typography variant="body1" gutterBottom>
                  Click to select a file or drag and drop
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Files will be uploaded to admin-uploads/
                </Typography>
              </Box>
            )}
          </Box>

          {selectedFile && !isExecuting && result?.success && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button
                variant="outlined"
                onClick={() => setShowResultPopup(true)}
              >
                View Upload Result
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <RunAdminCommandResultPopup
        open={showResultPopup}
        onClose={handleResultPopupClose}
        result={result}
        title="Upload Result"
      />
    </>
  );
}
